import { Avatar } from '@roger/ui-components';
import { Megaphone, Users } from 'lucide-react';
import type { InboxRow as InboxRowData } from '../../services/api';
import { initialsOf } from './initials';

interface InboxRowProps {
  row: InboxRowData;
  onOpen: (channelId: string) => void;
  /**
   * EMR-2272: the care-team thread for the chart on screen. Highlighted with
   * the tokens the sidebar already uses for its active item, so no new design
   * system variant is needed.
   */
  highlighted?: boolean;
}

function timeLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const when = new Date(iso);
  const today = new Date();
  const sameDay = when.toDateString() === today.toDateString();
  return sameDay
    ? when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function InboxRow({ row, onOpen, highlighted = false }: InboxRowProps) {
  const unread = row.unread_count ?? 0;
  // The server's marker, not the member count: a shared group or care team can
  // have one or two members and still gets the group icon.
  const isDirect = row.is_direct;

  return (
    <button
      type='button'
      onClick={() => onOpen(row.channel_id)}
      aria-current={highlighted ? 'true' : undefined}
      // The 10px `pl-2.5` plus the 2px rule keep the pinned row's content at the
      // same 12px inset that `px-3` gives every other row, so the avatar stays in
      // line.
      className={`flex w-full items-start gap-3 px-3 py-2 text-left focus:bg-muted ${
        highlighted
          ? 'border-l-2 border-primary pl-2.5 bg-accent/50 hover:bg-accent/30'
          : 'hover:bg-muted'
      }`}
    >
      {row.kind === 'announcement' ? (
        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
          <Megaphone className='h-4 w-4' />
        </span>
      ) : isDirect ? (
        <Avatar
          size='md'
          initials={initialsOf(row.title)}
          className='shrink-0'
        />
      ) : (
        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'>
          <Users className='h-4 w-4' />
        </span>
      )}

      <span className='min-w-0 flex-1'>
        <span className='flex items-baseline justify-between gap-2'>
          <span
            className={`truncate text-sm ${unread > 0 ? 'font-semibold' : ''}`}
          >
            {row.title}
          </span>
          <span className='shrink-0 text-[11px] text-muted-foreground'>
            {timeLabel(row.last_message?.created_at)}
          </span>
        </span>
        <span
          className={`block truncate text-xs ${unread > 0 ? 'text-foreground' : 'text-muted-foreground'}`}
        >
          {row.last_message
            ? `${row.last_message.sender_name}: ${row.last_message.preview}`
            : ''}
        </span>
      </span>

      {unread > 0 && (
        <span className='ml-1 shrink-0 rounded-full bg-destructive px-1.5 py-0.5 text-[11px] font-medium text-white'>
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  );
}
